import type { LocationTreeNode } from '../../../api/schedulingApis';
import type { TaskSnapshot } from '../../../stores/types';
import { isClosedIssue } from '../../../utils/linkedIssueHelpers';
import { parseDateSafe } from '../../../utils/utils';
import { getLeafTasks } from '../../dashboard/logic/prepareDashboardData';
import { isBlockLocationType } from '../../schedule-locations/logic/locationTypeRules';

export const UNASSIGNED_LOCATION_ID = '__unassigned__';

export type LocationHealthTaskRow = {
  id: string;
  name: string;
  progress: number;
  finish: string | null;
  overdue: boolean;
  openIssueCount: number;
};

export type LocationHealthRow = {
  locationId: string;
  name: string;
  locationType: string;
  taskCount: number;
  avgProgress: number;
  overdueCount: number;
  openIssueCount: number;
  tasks: LocationHealthTaskRow[];
};

export type LocationScheduleHealthKpis = {
  leafTaskCount: number;
  assignedTaskCount: number;
  assignmentCoveragePercent: number;
  unassignedTaskCount: number;
  locationsWithOverdue: number;
  locationsWithOpenIssues: number;
};

export type LocationScheduleHealthReport = {
  kpis: LocationScheduleHealthKpis;
  rows: LocationHealthRow[];
};

function asPercent(value: number | null | undefined): number {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  const scaled = n <= 1 ? n * 100 : n;
  return Math.max(0, Math.min(100, scaled));
}

function taskDisplayName(task: TaskSnapshot): string {
  const name = String(task.taskName ?? '').trim();
  return name || `Task ${task.id}`;
}

function openIssueCountForTask(task: TaskSnapshot): number {
  const linked = Array.isArray(task.linkedIssues) ? task.linkedIssues : [];
  if (linked.length > 0) {
    return linked.filter((issue) => !isClosedIssue(issue)).length;
  }
  return Math.max(0, Number(task.openIssueCount ?? 0) || 0);
}

function isOverdueTask(task: TaskSnapshot, now: Date): boolean {
  const finish = parseDateSafe(task.finish ?? null);
  if (!finish) return false;
  return finish < now && asPercent(task.percentComplete) < 100;
}

function toTaskRow(task: TaskSnapshot, now: Date): LocationHealthTaskRow {
  return {
    id: String(task.id),
    name: taskDisplayName(task),
    progress: Math.round(asPercent(task.percentComplete)),
    finish: task.finish ?? null,
    overdue: isOverdueTask(task, now),
    openIssueCount: openIssueCountForTask(task),
  };
}

function metricsFromTasks(tasks: TaskSnapshot[], now: Date): Omit<LocationHealthRow, 'locationId' | 'name' | 'locationType'> {
  const rows = tasks.map((t) => toTaskRow(t, now));
  const taskCount = rows.length;
  const avgProgress =
    taskCount > 0
      ? Math.round(rows.reduce((acc, r) => acc + r.progress, 0) / taskCount)
      : 0;
  return {
    taskCount,
    avgProgress,
    overdueCount: rows.filter((r) => r.overdue).length,
    openIssueCount: rows.reduce((acc, r) => acc + r.openIssueCount, 0),
    tasks: rows.sort((a, b) => {
      if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
      return a.name.localeCompare(b.name);
    }),
  };
}

/** Flatten tree and compute each node's descendant id set (including self). */
export function buildLocationDescendantMap(
  tree: LocationTreeNode[],
): Map<string, { node: LocationTreeNode; descendantIds: Set<string> }> {
  const map = new Map<string, { node: LocationTreeNode; descendantIds: Set<string> }>();

  const walk = (nodes: LocationTreeNode[]): Set<string> => {
    const ids = new Set<string>();
    for (const node of nodes) {
      const id = String(node.location_id ?? '').trim();
      if (!id) continue;
      const childIds = walk(Array.isArray(node.child) ? node.child : []);
      const descendantIds = new Set<string>([id, ...childIds]);
      map.set(id, { node, descendantIds });
      for (const childId of descendantIds) {
        ids.add(childId);
      }
    }
    return ids;
  };

  walk(tree);
  return map;
}

/**
 * Location health: KPIs + rolled-up location rows from leaf tasks + location tree.
 * Table rows are limited to block location types (descendant tasks still roll up into the block).
 */
export function deriveLocationScheduleHealth(
  tasks: TaskSnapshot[],
  locationTree: LocationTreeNode[],
  now: Date = new Date(),
): LocationScheduleHealthReport {
  const leafTasks = getLeafTasks(tasks);
  const leafTaskCount = leafTasks.length;

  const tasksByLocationId = new Map<string, TaskSnapshot[]>();
  const unassigned: TaskSnapshot[] = [];

  for (const task of leafTasks) {
    const locId = task.locationId != null ? String(task.locationId).trim() : '';
    if (!locId) {
      unassigned.push(task);
      continue;
    }
    const list = tasksByLocationId.get(locId) ?? [];
    list.push(task);
    tasksByLocationId.set(locId, list);
  }

  const assignedTaskCount = leafTaskCount - unassigned.length;
  const assignmentCoveragePercent =
    leafTaskCount > 0 ? Math.round((assignedTaskCount / leafTaskCount) * 100) : 0;

  const locationMap = buildLocationDescendantMap(locationTree);
  const rows: LocationHealthRow[] = [];

  for (const [locationId, { node, descendantIds }] of locationMap) {
    if (!isBlockLocationType(node.location_type)) continue;
    const rolled: TaskSnapshot[] = [];
    for (const id of descendantIds) {
      const bucket = tasksByLocationId.get(id);
      if (bucket) rolled.push(...bucket);
    }
    if (rolled.length === 0) continue;
    const metrics = metricsFromTasks(rolled, now);
    rows.push({
      locationId,
      name: String(node.name ?? '').trim() || locationId,
      locationType: String(node.location_type ?? '').trim() || '—',
      ...metrics,
    });
  }

  rows.sort((a, b) => {
    if (a.overdueCount !== b.overdueCount) return b.overdueCount - a.overdueCount;
    if (a.openIssueCount !== b.openIssueCount) return b.openIssueCount - a.openIssueCount;
    return a.name.localeCompare(b.name);
  });

  return {
    kpis: {
      leafTaskCount,
      assignedTaskCount,
      assignmentCoveragePercent,
      unassignedTaskCount: unassigned.length,
      locationsWithOverdue: rows.filter((r) => r.overdueCount > 0).length,
      locationsWithOpenIssues: rows.filter((r) => r.openIssueCount > 0).length,
    },
    rows,
  };
}
